import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import heic2any from "heic2any";
import dayjs from "dayjs";
import {
  CancellationReason,
  ENetwork,
  FlightCancellationReason,
} from "@/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const parseToHttps = (url: string | null) => {
  return url?.replace("http://", "https://");
};

export const convertHeicToJpeg = async (file: File) => {
  if (file.type === "image/heic") {
    const convertedBlob = await heic2any({ blob: file, toType: "image/png" });
    const convertedFile = new File(
      [convertedBlob as Blob],
      file.name.replace(/\.heic$/, ".png"),
      {
        type: "image/png",
      },
    );
    return convertedFile;
  }
  return file;
};

export const getNetworkDisplayName = (network: string): string => {
  switch (network) {
    case ENetwork.ETHEREUM:
      return "Ethereum";
    case ENetwork.SOLANA:
      return "Solana";
    case ENetwork.TON:
      return "Ton";
    default:
      return network;
  }
};

export const getCancellationPolicyText = (
  cancelRestrictionType: number,
  requestDate: string,
  checkInDate: string,
  cancelRestrictionDay: number,
  cancelRestrictionTime: string = "0000",
): string => {
  // Handle Non Refundable cases (type 0 or 1)
  if (cancelRestrictionType <= 1) {
    return "Non Refundable";
  }

  // Parse time from HHMM format
  const hours = parseInt(cancelRestrictionTime.substring(0, 2));
  const minutes = parseInt(cancelRestrictionTime.substring(2, 4));

  // Calculate threshold by subtracting days and setting specific time
  const checkInThreshold = dayjs(checkInDate)
    .subtract(cancelRestrictionDay, "day")
    .hour(hours)
    .minute(minutes)
    .second(0);

  const isBeforeThreshold = dayjs(requestDate).isBefore(checkInThreshold);

  // Handle type 2
  if (cancelRestrictionType === 2) {
    return isBeforeThreshold ? "Full refund" : "Refund with a cancellation fee";
  }

  // Handle type 3
  if (cancelRestrictionType === 3) {
    return isBeforeThreshold ? "Full refund" : "Non Refundable";
  }

  return "Unknown policy";
};

export interface ICancelSelection {
  label: string;
  value: CancellationReason;
}

export const cancelReason: ICancelSelection[] = [
  { label: "Change of Plans", value: CancellationReason.CHANGE_OF_PLANS },
  { label: "Booked Elsewhere", value: CancellationReason.BOOKED_ELSEWHERE },
  {
    label: "Inconvenient Location",
    value: CancellationReason.INCONVENIENT_LOCATION,
  },
  {
    label: "Error in Reservation",
    value: CancellationReason.ERROR_IN_RESERVATION,
  },
  {
    label: "Unforeseen Circumstances",
    value: CancellationReason.UNFORESEEN_CIRCUMSTANCES,
  },
];

export const getCancellationReasonLabel = (
  reason: CancellationReason,
): string => {
  const found = cancelReason.find((item) => item.value === reason);
  return found ? found.label : reason;
};

export interface IFlightCancelSelection {
  label: string;
  value: FlightCancellationReason;
}

export const flightCancelReason: IFlightCancelSelection[] = [
  { label: "Voluntary", value: FlightCancellationReason.VOLUNTARY },
  { label: "Flight Change", value: FlightCancellationReason.FLIGHT_CHANGE },
  {
    label: "Sickness or Death",
    value: FlightCancellationReason.SICKNESS_OR_DEATH,
  },
  { label: "Other", value: FlightCancellationReason.OTHER },
];

export const getFlightCancellationReasonLabel = (
  reason: FlightCancellationReason,
): string => {
  const found = flightCancelReason.find((item) => item.value === reason);
  return found ? found.label : reason;
};

export const getUsernameFromJwt = (token?: string): string | undefined => {
  try {
    if (!token) return undefined;
    const parts = token.split(".");
    if (parts.length < 2) return undefined;

    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    );

    const json = atob(padded);
    const payload = JSON.parse(json) as Record<string, unknown>;

    const username =
      typeof payload.username === "string" ? payload.username : undefined;
    return username;
  } catch (error) {
    console.error(error);
    return undefined;
  }
};
